import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { WalkthroughOverlay } from "./WalkthroughOverlay";
import { trackWalkthrough } from "./walkthroughAnalytics";
import { walkthroughSteps } from "./walkthroughSteps";
import { walkthroughStorage } from "./walkthroughStorage";
import { WalkthroughContextValue } from "./types";

export const WalkthroughContext = createContext<WalkthroughContextValue | null>(
  null,
);

const AUTO_START_BLOCKED_ROUTES = [
  "/",
  "/login",
  "/auth/callback",
  "/subscription",
  "/payment",
  "/payment-status",
  "/change-password",
];

const DESKTOP_WALKTHROUGH_QUERY = "(min-width: 1025px)";

const getRequiredSelectionType = (
  step: (typeof walkthroughSteps)[number] | null,
) => {
  if (!step?.requiresSelection) return null;
  if (step.id === "choose-course") return "course";
  if (step.id === "choose-class") return "class";
  if (step.id === "choose-subject") return "subject";
  if (step.id === "choose-chapter") return "chapter";
  return null;
};

const getIsWalkthroughDesktop = () => {
  if (typeof window === "undefined") return true;
  return window.matchMedia(DESKTOP_WALKTHROUGH_QUERY).matches;
};

const useIsWalkthroughDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(getIsWalkthroughDesktop);

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_WALKTHROUGH_QUERY);
    const handleChange = () => setIsDesktop(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return isDesktop;
};

export function WalkthroughProvider({ children }: { children: ReactNode }) {
  const { user, activeProfile, isAuthenticated, isLoading, isSubscribed } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isWalkthroughDesktop = useIsWalkthroughDesktop();
  const userId = String(
    (activeProfile as any)?._id ||
      (activeProfile as any)?.id ||
      (user as any)?._id ||
      (user as any)?.id ||
      "default",
  );
  const backendWalkthroughShown = (user as any)?.walkthrough_shown === true;
  const loginCount = Number((user as any)?.loginCount ?? 0);
  const isFirstLoginUser = loginCount > 0 && loginCount <= 1;
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [selectionMessage, setSelectionMessage] = useState<string | null>(null);
  const manualReplay = useRef(false);
  const activeRef = useRef(false);
  const autoLaunchedThisSession = useRef(false);

  const baseStep = walkthroughSteps[currentStepIndex] ?? null;
  const currentStep = useMemo(() => {
    if (!baseStep) return null;
    if (baseStep.id !== "welcome") return baseStep;
    return {
      ...baseStep,
      description: isSubscribed
        ? "Everything in Ekluvya is available with your subscription. Let’s take a quick tour so you know where everything is."
        : "Welcome! Take a quick tour so you know where everything is. After subscription, Ekluvya unlocks the full learning experience for you.",
    };
  }, [baseStep, isSubscribed]);

  const hasRequiredSelection = useCallback((step: typeof baseStep) => {
    if (!step?.requiresSelection) return true;
    if (step.id === "choose-course") {
      return Boolean(
        document.querySelector('[data-walkthrough-selected="course"]') ||
        document.querySelector('[data-walkthrough="class-selector"]'),
      );
    }
    if (step.id === "choose-class") {
      return Boolean(
        document.querySelector('[data-walkthrough-selected="class"]') ||
        document.querySelector('[data-walkthrough="subject-selector"]'),
      );
    }
    if (step.id === "choose-subject") {
      return Boolean(
        document.querySelector('[data-walkthrough-selected="subject"]') ||
        document.querySelector('[data-walkthrough="chapter-selector"]'),
      );
    }
    if (step.id === "choose-chapter") {
      return Boolean(
        document.querySelector('[data-walkthrough-selected="chapter"]') ||
        document.querySelector('[data-walkthrough="teacher-videos"]'),
      );
    }
    return true;
  }, []);

  const viewStep = useCallback(
    (index: number) => {
      const step = walkthroughSteps[index];
      if (!step) return;
      setSelectionMessage(null);
      setCurrentStepIndex(index);
      if (!manualReplay.current) {
        walkthroughStorage.write(userId, { currentStep: index });
      }
      trackWalkthrough("walkthrough_step_viewed", step, index + 1);
    },
    [userId],
  );

  const start = useCallback(
    (manual = true) => {
      if (!isWalkthroughDesktop) return;

      manualReplay.current = manual;
      const saved = walkthroughStorage.read(userId);
      const startAt = manual
        ? 0
        : Math.min(saved.currentStep, walkthroughSteps.length - 1);
      setIsActive(true);
      activeRef.current = true;
      trackWalkthrough(
        "walkthrough_started",
        walkthroughSteps[startAt],
        startAt + 1,
      );
      viewStep(startAt);
    },
    [isWalkthroughDesktop, userId, viewStep],
  );

  const complete = useCallback(() => {
    trackWalkthrough(
      "walkthrough_completed",
      walkthroughSteps[currentStepIndex],
      currentStepIndex + 1,
    );
    if (!manualReplay.current) {
      walkthroughStorage.write(userId, {
        shown: true,
        completed: true,
        skipped: false,
        currentStep: walkthroughSteps.length - 1,
        completedAt: new Date().toISOString(),
      });
    }
    setIsActive(false);
    activeRef.current = false;
    manualReplay.current = false;
  }, [currentStepIndex, userId]);

  const next = useCallback(() => {
    const step = walkthroughSteps[currentStepIndex];
    if (!hasRequiredSelection(step)) {
      setSelectionMessage(
        step.selectionMissingMessage ||
          "Please select the highlighted item to continue.",
      );
      return;
    }
    trackWalkthrough("walkthrough_next", step, currentStepIndex + 1);
    if (currentStepIndex >= walkthroughSteps.length - 1) {
      complete();
      return;
    }
    viewStep(currentStepIndex + 1);
  }, [complete, currentStepIndex, hasRequiredSelection, viewStep]);

  const advanceAfterSelection = useCallback(
    (stepIndex: number, attempt = 0) => {
      const step = walkthroughSteps[stepIndex];
      if (!activeRef.current || stepIndex !== currentStepIndex) return;
      if (hasRequiredSelection(step)) {
        trackWalkthrough("walkthrough_next", step, stepIndex + 1);
        if (stepIndex >= walkthroughSteps.length - 1) {
          complete();
          return;
        }
        viewStep(stepIndex + 1);
        return;
      }
      if (attempt < 12) {
        window.setTimeout(
          () => advanceAfterSelection(stepIndex, attempt + 1),
          250,
        );
      }
    },
    [complete, currentStepIndex, hasRequiredSelection, viewStep],
  );

  const previous = useCallback(() => {
    trackWalkthrough(
      "walkthrough_previous",
      walkthroughSteps[currentStepIndex],
      currentStepIndex + 1,
    );
    viewStep(Math.max(0, currentStepIndex - 1));
  }, [currentStepIndex, viewStep]);

  const skip = useCallback(() => {
    trackWalkthrough(
      "walkthrough_skipped",
      walkthroughSteps[currentStepIndex],
      currentStepIndex + 1,
    );
    if (!manualReplay.current) {
      walkthroughStorage.write(userId, {
        shown: true,
        skipped: true,
        completed: false,
        currentStep: currentStepIndex,
        skippedAt: new Date().toISOString(),
      });
    }
    setIsActive(false);
    activeRef.current = false;
    manualReplay.current = false;
  }, [currentStepIndex, userId]);

  useEffect(() => {
    activeRef.current = isActive;
  }, [isActive]);

  useEffect(() => {
    if (!isWalkthroughDesktop && isActive) {
      setIsActive(false);
      activeRef.current = false;
      manualReplay.current = false;
    }
  }, [isActive, isWalkthroughDesktop]);

  useEffect(() => {
    if (
      isLoading ||
      isActive ||
      !isWalkthroughDesktop ||
      !isAuthenticated ||
      AUTO_START_BLOCKED_ROUTES.includes(location.pathname)
    ) {
      return;
    }
    const hasSubscriptionWalkthroughRequest =
      sessionStorage.getItem("ekluvya.walkthrough.pending") === "1";
    const shouldAutoLaunch =
      hasSubscriptionWalkthroughRequest ||
      (isFirstLoginUser &&
        !backendWalkthroughShown &&
        !autoLaunchedThisSession.current);

    if (shouldAutoLaunch) {
      const timer = window.setTimeout(() => {
        if (
          autoLaunchedThisSession.current &&
          sessionStorage.getItem("ekluvya.walkthrough.pending") !== "1"
        ) {
          return;
        }
        autoLaunchedThisSession.current = true;
        sessionStorage.removeItem("ekluvya.walkthrough.pending");
        walkthroughStorage.write(userId, {
          shown: true,
          completed: false,
          skipped: false,
          currentStep: 0,
          completedAt: null,
          skippedAt: null,
        });
        const token =
          sessionStorage.getItem("token") || localStorage.getItem("token");
        if (token) {
          void fetch(
            `${process.env.REACT_APP_API_BASE_URL}/auth/walkthrough-shown`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            },
          ).catch((error) => {
            console.warn("Unable to save walkthrough status", error);
          });
        }
        start(false);
      }, 500);
      return () => window.clearTimeout(timer);
    }
  }, [
    isAuthenticated,
    isActive,
    isWalkthroughDesktop,
    isLoading,
    location.pathname,
    backendWalkthroughShown,
    isFirstLoginUser,
    start,
    userId,
  ]);

  useEffect(() => {
    if (!isActive || !currentStep?.route) return;
    const isCurrentRoute =
      location.pathname === currentStep.route ||
      location.pathname.startsWith(`${currentStep.route}/`);
    if (!isCurrentRoute) {
      navigate(currentStep.route);
    }
  }, [currentStep, isActive, location.pathname, navigate]);

  useEffect(() => {
    if (!isActive) return;
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") skip();
      if (event.key === "ArrowRight") next();
      if (event.key === "ArrowLeft") previous();
    };
    window.addEventListener("keydown", keydown);
    return () => window.removeEventListener("keydown", keydown);
  }, [isActive, next, previous, skip]);

  useEffect(() => {
    if (!isActive || !currentStep?.requiresSelection) return;
    const requiredType = getRequiredSelectionType(currentStep);
    if (!requiredType) return;

    const handleChoiceClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const choice = target?.closest<HTMLElement>(
        `[data-walkthrough-choice="${requiredType}"]`,
      );
      if (!choice) return;
      const stepIndex = currentStepIndex;
      window.setTimeout(() => advanceAfterSelection(stepIndex), 150);
    };

    document.addEventListener("click", handleChoiceClick, true);
    return () => document.removeEventListener("click", handleChoiceClick, true);
  }, [advanceAfterSelection, currentStep, currentStepIndex, isActive]);

  useEffect(() => {
    const abandon = () => {
      if (activeRef.current) {
        trackWalkthrough(
          "walkthrough_abandoned",
          walkthroughSteps[currentStepIndex],
          currentStepIndex + 1,
        );
      }
    };
    window.addEventListener("pagehide", abandon);
    return () => window.removeEventListener("pagehide", abandon);
  }, [currentStepIndex]);

  const value = useMemo<WalkthroughContextValue>(
    () => ({
      isActive: isActive && isWalkthroughDesktop,
      currentStepIndex,
      currentStep,
      totalSteps: walkthroughSteps.length,
      start,
      next,
      previous,
      skip,
      complete,
    }),
    [
      complete,
      currentStep,
      currentStepIndex,
      isActive,
      isWalkthroughDesktop,
      next,
      previous,
      skip,
      start,
    ],
  );

  return (
    <WalkthroughContext.Provider value={value}>
      {children}
      {isActive && isWalkthroughDesktop && currentStep && (
        <WalkthroughOverlay
          step={currentStep}
          stepNumber={currentStepIndex + 1}
          totalSteps={walkthroughSteps.length}
          onNext={next}
          onPrevious={previous}
          onSkip={skip}
          selectionMessage={selectionMessage}
        />
      )}
    </WalkthroughContext.Provider>
  );
}
